import { useEffect, useCallback, useRef } from 'react'
import { useToast } from '@t-one-internal/design-system'
import { useRecognitionStore } from '../stores/recognitionStore'
import { useEmployeeProfileStore } from '../stores/employeeProfileStore'
import { useAuth } from '../context/AuthContextBackend'
import { recognitionService } from '../services/recognitionService'
import type { CompanyValue } from '../types/recognition'

/**
 * Data-fetching hook for the Recognition feature.
 * Call ONLY in RecognitionPage — it triggers useEffects for initial data loading.
 */
export function useRecognitionData() {
  const { toast } = useToast()
  const hasLoadedOnce = useRef(false)

  const { user } = useAuth()
  const feedFilter = useRecognitionStore((s) => s.feedFilter)
  const feedView = useRecognitionStore((s) => s.feedView)
  const feedPage = useRecognitionStore((s) => s.feedPage)
  const feedVersion = useRecognitionStore((s) => s.feedVersion)
  const primaryOrgUnitId = useEmployeeProfileStore((s) => s.selectedEmployee?.primary_org_unit_id)
  const loadProfile = useEmployeeProfileStore((s) => s.loadProfile)
  const selectedEmployee = useEmployeeProfileStore((s) => s.selectedEmployee)
  const setRecognitions = useRecognitionStore((s) => s.setRecognitions)
  const setFeedLoading = useRecognitionStore((s) => s.setFeedLoading)
  const setFeedTotalPages = useRecognitionStore((s) => s.setFeedTotalPages)
  const setMyStats = useRecognitionStore((s) => s.setMyStats)
  const setStatsLoading = useRecognitionStore((s) => s.setStatsLoading)
  const setTrendingValues = useRecognitionStore((s) => s.setTrendingValues)
  const setTopRecognized = useRecognitionStore((s) => s.setTopRecognized)
  const setDashboardLoading = useRecognitionStore((s) => s.setDashboardLoading)
  const setCompanyValues = useRecognitionStore((s) => s.setCompanyValues)
  const setError = useRecognitionStore((s) => s.setError)

  const fetchFeed = useCallback(async () => {
    if (!hasLoadedOnce.current) setFeedLoading(true)
    setError(null)
    try {
      const unitId = feedView === 'my_team' && primaryOrgUnitId ? String(primaryOrgUnitId) : undefined
      const result = await recognitionService.getRecognitions({
        type: feedFilter === 'all' ? undefined : feedFilter,
        view: feedView,
        page: feedPage,
        perPage: 20,
        unit_id: unitId,
      })
      setRecognitions(result.data)
      setFeedTotalPages(result.meta.total_pages)
    } catch {
      const message = 'Failed to load recognition feed'
      setError(message)
      toast({ title: 'Error', description: message, variant: 'destructive' })
    } finally {
      hasLoadedOnce.current = true
      setFeedLoading(false)
    }
  }, [feedFilter, feedView, feedPage, feedVersion, primaryOrgUnitId, setRecognitions, setFeedLoading, setFeedTotalPages, setError, toast])

  const fetchDashboard = useCallback(async () => {
    setStatsLoading(true)
    setDashboardLoading(true)
    try {
      const dashboard = await recognitionService.getDashboard()
      setMyStats(dashboard.my_stats)
      setTrendingValues(dashboard.trending_values)
      setTopRecognized(dashboard.top_recognized)
    } catch {
      // Non-critical — sidebar data
    } finally {
      setStatsLoading(false)
      setDashboardLoading(false)
    }
  }, [feedVersion, setMyStats, setStatsLoading, setTrendingValues, setTopRecognized, setDashboardLoading])

  const fetchCompanyValues = useCallback(async () => {
    try {
      const values = await recognitionService.getCompanyValues()
      setCompanyValues(values.map((v) => v.name) as CompanyValue[])
    } catch {
      // Non-critical
    }
  }, [setCompanyValues])

  // Load employee profile if not already loaded (needed for unit_id filtering)
  useEffect(() => {
    if (!selectedEmployee && user?.user_uuid) {
      loadProfile(user.user_uuid)
    }
  }, [selectedEmployee, user?.user_uuid, loadProfile])

  // Auto-fetch feed when filter/page changes
  useEffect(() => {
    fetchFeed()
  }, [fetchFeed])

  // Fetch dashboard data (stats + trending + top recognized) on mount
  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  useEffect(() => {
    fetchCompanyValues()
  }, [fetchCompanyValues])

  return { fetchFeed, fetchDashboard, fetchCompanyValues }
}
